// DOLFI — Loader sicuro per l'upsell del carrello.
// Fetcha la sezione raccomandazioni UNA volta (complementary, poi fallback related),
// inietta le card, nasconde il modulo se vuoto. Nessun MutationObserver sul self
// (evita il loop infinito del componente nativo product-recommendations.js con risultati vuoti).
class CartUpsellComponent extends HTMLElement {
  #loading = false;
  #lastKey = '';

  connectedCallback() {
    this.track = this.querySelector('[data-upsell-track]');

    // ricarica quando il carrello cambia (es. dopo add-to-cart che apre il drawer)
    // rebase sull'ULTIMO articolo presente nel carrello (valido sempre, anche dopo una rimozione)
    this._onCart = async () => {
      try {
        const cart = await fetch(`${this.#root}cart.js`).then((r) => r.json());
        if (cart && cart.item_count > 0) {
          this._baseProduct = String(cart.items[cart.items.length - 1].product_id);
        } else {
          this._baseProduct = null;
        }
      } catch (e) {
        /* mantiene il base precedente */
      }
      this.dataset.loaded = '';
      this.#load();
    };
    document.addEventListener('cart:update', this._onCart);

    // popup opzioni (varianti) dentro il cart, senza uscire
    this.addEventListener('click', this.#onOptionsClick);

    // trigger affidabile: carica quando il dialog del drawer si apre
    this.dialog = this.closest('dialog');
    if (this.dialog) {
      if (this.dialog.open) this.#load();
      this._dlgObs = new MutationObserver(() => {
        if (this.dialog.open) this.#load();
      });
      this._dlgObs.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    } else {
      // fuori da un dialog: lazy-load quando visibile
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.io.disconnect();
            this.#load();
          }
        },
        { rootMargin: '0px 0px 200px 0px' }
      );
      this.io.observe(this);
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this._dlgObs?.disconnect();
    document.removeEventListener('cart:update', this._onCart);
    this.removeEventListener('click', this.#onOptionsClick);
  }

  // Selettore varianti CUSTOM (no theme variant-picker → niente revert).
  // Alimentato dal JSON prodotto, stato gestito qui, add via /cart/add.js.
  #onOptionsClick = async (event) => {
    const btn = event.target.closest('[data-upsell-options]');
    if (!btn) return;
    event.preventDefault();

    const handle = btn.dataset.productHandle;
    const dialog = document.getElementById('quick-add-dialog');
    const content = document.getElementById('quick-add-modal-content');
    if (!handle || !dialog || !content) return;

    const original = btn.textContent;
    btn.setAttribute('disabled', '');
    try {
      const res = await fetch(`${this.#root}products/${handle}.js`);
      if (!res.ok) throw new Error('fetch failed');
      const product = await res.json();
      this.#buildChooser(product, btn.dataset.productImage, content);

      if (typeof dialog.showDialog === 'function') dialog.showDialog();
      else dialog.querySelector('dialog')?.showModal();
    } catch (e) {
      if (handle) window.location.href = `${this.#root}products/${handle}`;
    } finally {
      btn.removeAttribute('disabled');
      btn.textContent = original;
    }
  };

  #money(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  #buildChooser(product, imgSrc, content) {
    const variants = product.variants || [];
    let current = variants.find((v) => v.available) || variants[0];

    const wrap = document.createElement('div');
    wrap.className = 'cart-upsell__chooser';

    if (imgSrc || (current && current.featured_image)) {
      const fig = document.createElement('div');
      fig.className = 'cart-upsell__popup-media';
      const im = document.createElement('img');
      im.src = imgSrc || current.featured_image.src;
      im.alt = product.title || '';
      fig.appendChild(im);
      wrap.appendChild(fig);
    }

    const title = document.createElement('p');
    title.className = 'cart-upsell__popup-title';
    title.textContent = product.title || '';
    wrap.appendChild(title);

    const priceEl = document.createElement('p');
    priceEl.className = 'cart-upsell__popup-price';
    wrap.appendChild(priceEl);

    // un <select> per ogni opzione (se più di una variante "Default Title" salta)
    const selects = [];
    const hasRealOptions = !(product.options.length === 1 && product.options[0].toLowerCase() === 'title');
    if (hasRealOptions) {
      product.options.forEach((optName, idx) => {
        const field = document.createElement('div');
        field.className = 'cart-upsell__opt';
        const label = document.createElement('label');
        label.textContent = typeof optName === 'object' ? optName.name : optName;
        const sel = document.createElement('select');
        const values = [...new Set(variants.map((v) => v.options[idx]))];
        values.forEach((val) => {
          const o = document.createElement('option');
          o.value = val;
          o.textContent = val;
          sel.appendChild(o);
        });
        sel.value = current.options[idx];
        sel.addEventListener('change', () => update());
        field.appendChild(label);
        field.appendChild(sel);
        wrap.appendChild(field);
        selects.push(sel);
      });
    }

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'button cart-upsell__popup-add';
    wrap.appendChild(addBtn);

    const update = () => {
      if (selects.length) {
        const chosen = selects.map((s) => s.value);
        const match = variants.find((v) => chosen.every((val, i) => v.options[i] === val));
        if (match) current = match;
      }
      // prezzo + compare
      priceEl.innerHTML = '';
      if (current.compare_at_price && current.compare_at_price > current.price) {
        const s = document.createElement('s');
        s.textContent = this.#money(current.compare_at_price);
        s.className = 'cart-upsell__popup-was';
        priceEl.appendChild(s);
      }
      priceEl.appendChild(document.createTextNode(' ' + this.#money(current.price)));
      // disponibilità
      if (current.available) {
        addBtn.disabled = false;
        addBtn.textContent = this.dataset.i18nAddToCart || 'Add to cart';
      } else {
        addBtn.disabled = true;
        addBtn.textContent = this.dataset.i18nSoldOut || 'Sold out';
      }
    };
    update();

    addBtn.addEventListener('click', async () => {
      if (!current || !current.available) return;
      addBtn.disabled = true;
      addBtn.textContent = this.dataset.i18nAdding || 'Adding…';
      try {
        const r = await fetch(`${this.#root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: current.id, quantity: 1 }] }),
        });
        if (!r.ok) throw new Error('add failed');
        // aggiorna il drawer + rebase upsell sull'ultimo aggiunto
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { source: 'cart-upsell', productId: String(product.id) } },
          })
        );
        const dlg = document.getElementById('quick-add-dialog');
        if (typeof dlg?.closeDialog === 'function') dlg.closeDialog();
        else dlg?.querySelector('dialog')?.close();
      } catch (e) {
        addBtn.disabled = false;
        addBtn.textContent = this.dataset.i18nRetry || 'Try again';
      }
    });

    content.replaceChildren(wrap);
  }

  get #root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  async #fetchSection(intent) {
    const pid = this._baseProduct || this.dataset.productId;
    if (!pid) return '';
    const limit = this.dataset.limit || 6;
    const section = this.dataset.sectionId || 'cart-recommendations';
    const url = `${this.#root}recommendations/products?section_id=${section}&product_id=${pid}&intent=${intent}&limit=${limit}`;
    const res = await fetch(url);
    if (!res.ok) return '';
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const track = doc.querySelector('[data-upsell-track]');
    return track ? track.innerHTML.trim() : '';
  }

  async #load() {
    const pid = this._baseProduct || this.dataset.productId;
    if (this.#loading || !pid) return;
    const key = `${pid}:${this.dataset.limit || 6}`;
    if (key === this.#lastKey && this.dataset.loaded === 'true') return;

    this.#loading = true;
    try {
      let html = await this.#fetchSection('complementary');
      if (!html) html = await this.#fetchSection('related');

      if (html && this.track) {
        this.track.innerHTML = html;
        this.dataset.loaded = 'true';
        this.#lastKey = key;
        this.classList.remove('cart-upsell--loading');
        this.style.display = '';
      } else {
        this.style.display = 'none';
      }
    } catch (e) {
      this.style.display = 'none';
    } finally {
      this.#loading = false;
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
